import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { VTableHeaderType } from "@app/directives/_layout/v-table/_enums/VTableHeaderType.enum";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableCell } from "@app/directives/_layout/v-table/_models/IVTableCell";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { FallbackCellType, IDateCellOptions, VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { SortType } from "@app/enum/SortType";
import { UnitConversionService, UnitConversionType } from "@app/services/unitConversion.service";
import { IFilterBarTab } from "@directives/_resourceFilters/filter-bar/filter-bar.models";
import { SortDirection } from "@enums/SortDirection";
import { AudioService } from "@services/audio.service";
import { DateService, DateUnit } from "@services/date.service";
import { ReasonCodeService } from "@services/reasonCode.service";
import { TranslationService } from "@services/translation.service";
import { VehicleService } from "@services/vehicle.service";
import { ITemperatureOutOfBoundsHistory } from "@temperature/_models/temperature/ITemperatureOutOfBoundsHistory";
import { ITemperatureProbeWithLatestRecord, ITemperatureProbeWithTemperatureData } from "@temperature/_models/temperature/ITemperatureProbe";
import { ITemperatureRecord } from "@temperature/_models/temperature/ITemperatureRecord";
import { IVehicleWithTemperatureData } from "@temperature/_models/temperature/IVehicleWithTemperatureData";
import { TemperatureOutOfBoundsHistoryService } from "@temperature/_services/temperature-out-of-bounds.service";
import { TemperatureProbeService } from "@temperature/_services/temperature-probe.service";
import { TemperatureRecordService } from "@temperature/_services/temperature-record.service";
import { lastValueFrom } from "rxjs";
import { IStackedProbeReadingData, StackedProbeReadingComponent } from "../stacked-probe-reading/stacked-probe-reading.component";
import "./temperature-compliance.component.scss";

@Component({
    selector: 'temperature-compliance',
    template: require("./temperature-compliance.component.html")
})
export class TemperatureComplianceComponent implements OnInit {
    loading = false;

    filterTabs: IFilterBarTab<IVehicleWithTemperatureData>[] = [];

    temperatureRecords: ITemperatureRecord[];
    temperatureProbes: ITemperatureProbeWithLatestRecord[];
    vehicles: IVehicleWithTemperatureData[];
    activeOutOfBoundsHistories: ITemperatureOutOfBoundsHistory[];

    tableColumns: IVTableColumn<IVehicleWithTemperatureData>[];
    tableRows: IVTableRow[];

    selectedVehicle: IVehicleWithTemperatureData;

    temperatureData: any[] = [];
    reloadInterval: ReturnType<typeof setInterval>;

    useAudibleAlarm: boolean = false;
    latestUpdateTime: Date;

    dateCellOptions: IDateCellOptions;
    totalVisibleColumns: number = 4;
    maxVisibleColumns: number = 8;

    totalExportableProbeColumns: number = 4;

    SortDirection = SortDirection;

    constructor(
        private readonly audioService: AudioService,
        private readonly conversionService: UnitConversionService,
        private readonly dateService: DateService,
        private readonly reasonCodeService: ReasonCodeService,
        private readonly temperatureOutOfBoundsHistoryService: TemperatureOutOfBoundsHistoryService,
        private readonly temperatureProbeService: TemperatureProbeService,
        private readonly temperatureRecordsService: TemperatureRecordService,
        private readonly translationService: TranslationService,
        private readonly vehicleService: VehicleService,
        private readonly vTableService: VTableService,
        private readonly changeDetector: ChangeDetectorRef) { }


    ngOnInit(): void {
        this.createTabs();
        this.getData(true);
        this.latestUpdateTime = new Date();
        this.reloadInterval = setInterval(() => {
            this.getData();
        }, 70_000)
    }

    ngOnDestroy(): void {
        clearInterval(this.reloadInterval);
    }

    toggleAudibleAlarm() {
        this.useAudibleAlarm = !this.useAudibleAlarm;
    }

    onRowClicked($event): void {
        this.selectedVehicle = $event.data;
    }

    //--

    private getVehicleIsInJourney(vehicle: IVehicleWithTemperatureData) {
        vehicle.inJourney = this.reasonCodeService.isTravelling(vehicle.Plot?.Reason) && vehicle.Plot?.Velocity > 0;
        vehicle.journeyStatus = vehicle.inJourney ? this.translationService.t("TempCompliance.InJourney") : '';
    }

    private getData(firstLoad = false): Promise<void> {
        if (firstLoad) {
            this.loading = true;
        }
        
        const promises: any[] = [];

        promises.push(lastValueFrom(this.temperatureRecordsService.getLatestTemperatureRecords(false)));
        promises.push(lastValueFrom(this.temperatureProbeService.getTemperatureProbes()));
        promises.push(this.vehicleService.getVehicles({ includeLatestPlots: true, includePeripheralMappings: true }));
        promises.push(lastValueFrom(this.temperatureOutOfBoundsHistoryService.getActiveVehicleTemperatureOutOfBoundsHistories()))

        return Promise.all(promises).then(([tempRecords, probes, vehicles, outOfBoundsHistories]) => {
            this.temperatureRecords = tempRecords;
            this.temperatureProbes = probes;
            this.vehicles = vehicles;
            this.activeOutOfBoundsHistories = outOfBoundsHistories;

            this.mapData();

            if (this.useAudibleAlarm) {
                this.checkForNewOutOfBounds();
            }
            
            this.createTable();
        }).finally(() => {
            this.tableRows = this.vTableService.createTableRows(this.vehicles, this.tableColumns);
            this.latestUpdateTime = new Date();
            this.loading = false;

            this.changeDetector.markForCheck();
        })
    }

    private mapData(): void {
        if (!this.vehicles) {
            return;
        }

        this.setupProbes();

        this.temperatureProbeService.mapTemperatureProbesToVehicles(this.temperatureProbes, this.vehicles);

        this.vehicles = this.vehicles.filter(vehicle => vehicle.temperatureProbes?.length > 0);

        this.vehicles.forEach(vehicle => {
            if (!vehicle.temperatureProbes.length) {
                return;
            }

            vehicle.latestTemperatureUpdate = new Date(this.temperatureProbeService.getLastUpdatedForVehicleProbes(vehicle.temperatureProbes));
            vehicle.lastUpdatedRelativeTime = this.dateService.relativeTime(vehicle.latestTemperatureUpdate);

            this.getVehicleIsInJourney(vehicle);
            this.getVehicleIsInErrorState(vehicle);
            this.checkVehicleHasProfileSet(vehicle);
            this.cleanVehicleProbeNames(vehicle);
            this.getVehicleIsInJourney(vehicle);

            if (vehicle.temperatureProbes.length > this.totalVisibleColumns) {
                this.totalVisibleColumns = Math.min(vehicle.temperatureProbes.length, this.maxVisibleColumns);
            }

            if (vehicle.temperatureProbes.length > this.totalExportableProbeColumns) {
                this.totalExportableProbeColumns = vehicle.temperatureProbes.length;
            }
        })


    }

    private setupProbes(): void {
        this.temperatureProbes.sort((a, b) => a.Position - b.Position);

        this.temperatureProbes.forEach(probe => {
            this.temperatureProbeService.mapProbeLatestRecord(probe, this.temperatureRecords);
            this.temperatureOutOfBoundsHistoryService.mapHistoriesToProbe(this.activeOutOfBoundsHistories, probe);
        });

        this.temperatureProbes = this.temperatureProbes.filter(probe => !!probe.latestRecord && this.probeIsActive(probe));
    }

    private cleanVehicleProbeNames(vehicle: IVehicleWithTemperatureData) {
        vehicle.temperatureProbes.forEach(probe => {
            this.temperatureProbeService.cleanVehicleProbeName(vehicle.Reg, probe);
        })
    }

    private checkForNewOutOfBounds(): void {
        if (!this.activeOutOfBoundsHistories) {
            return;
        }

        let alarmSounded = false;
        this.activeOutOfBoundsHistories.forEach(history => {
            if (alarmSounded) {
                return;
            }
            if (this.dateService.date1IsAfterDate2(history.StartUtc, this.latestUpdateTime)) {
                this.audioService.soundAlarm();
                alarmSounded = true;
            }
        })
    }

    private probeIsActive(probe: ITemperatureProbeWithTemperatureData): boolean {
        if (!probe) {
            return false;
        }
        if (probe.latestRecord && this.isRecentData(probe.latestRecord.Timestamp)) {
            return true;
        }

        return false;
    }

    private isRecentData(date: Date): boolean {
        const cutoff = this.dateService.addOrSubtractTime(new Date(), -7, DateUnit.Day);

        if (this.dateService.date1IsAfterDate2(cutoff, date)) {
            return false;
        }

        return true;
    }

    private getVehicleIsInErrorState(vehicle: IVehicleWithTemperatureData) {
        let errorState: VTableRowStatus = VTableRowStatus.Info;
        if (this.vehicleHasProbeOutOfBounds(vehicle)) {
            errorState = VTableRowStatus.Error;
            vehicle.outOfBounds = true;
        }

        const thirtyMinutesAgo = this.dateService.addOrSubtractTime(new Date(), -30, DateUnit.Min);
        if (this.dateService.date1IsAfterDate2(thirtyMinutesAgo, vehicle.latestTemperatureUpdate)) {
            errorState = VTableRowStatus.Error;
            vehicle.outOfDate = true;
        }

        vehicle.errorStatus = errorState;
    }

    private checkVehicleHasProfileSet(vehicle: IVehicleWithTemperatureData) {
        vehicle.hasProfile = vehicle.temperatureProbes.some(probe => !!probe.TemperatureProfile);
    }

    private vehicleHasProbeOutOfBounds(vehicle: IVehicleWithTemperatureData) {
        return this.activeOutOfBoundsHistories.some(history => {
            return !!vehicle.temperatureProbes.find(probe => probe.Id === history.TemperatureProbeId);
        });
    }


    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader({ minTableWidth: 900 }),
                createCellFunction: (vehicle) => this.vTableService.createStatusCell(vehicle.errorStatus)
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: (vehicle) => this.vTableService.createVehicleRegCell(vehicle)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("TempCompliance.State"), minTableWidth: 900 }),
                createCellFunction: (vehicle) => this.vTableService.createTableCell(vehicle.journeyStatus)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t("Common.LastUpdated"), minTableWidth: 900, width: 130, sortType: SortType.Date }, VTableHeaderType.Date),
                createCellFunction: (vehicle) => this.vTableService.createDateCell(
                    vehicle.latestTemperatureUpdate,
                    {
                        showRelativeTime: true,
                        showErrorIfBeforeDate: this.dateService.addOrSubtractTime(new Date(), -30, DateUnit.Min),
                        showWarningIfBeforeDate: this.dateService.addOrSubtractTime(new Date(), -14, DateUnit.Min)
                    })
            }
        ];

        for (let i = 0; i < this.totalExportableProbeColumns; i++) {
            let minTableWidth: number;
            if (i >= 4) {
                minTableWidth = 900;
            }
            const exportOnly = i >= this.totalVisibleColumns;

            this.tableColumns.push({
                header: this.vTableService.createTableHeader({ title: this.translationService.t("TempCompliance.ProbeX", i + 1), minTableWidth, sortType: SortType.Number, exportOnly: exportOnly }),
                createCellFunction: (vehicle) => this.createTemperatureReadingCell(vehicle, i)
            })
        }
    }



    private createTemperatureReadingCell(vehicle, index: number): IVTableCell {
        if (!vehicle.temperatureProbes[index]) {

            return this.vTableService.createTableCell(null, {}, FallbackCellType.Dashed);
        }

        const latestTemp = vehicle.temperatureProbes[index].latestRecord.Temperature;
        const cutOff = this.dateService.addOrSubtractTime(new Date(), -30, DateUnit.Min);

        const data: IStackedProbeReadingData = {
            probeName: vehicle.temperatureProbes[index].cleanName,
            temperature: this.conversionService.smartConvert(latestTemp, UnitConversionType.Temperature, { precision: 1 }),
            outOfBounds: this.probeIsOutOfBounds(vehicle.temperatureProbes[index]),
            lastUpdated: `${this.translationService.t('Common.LastUpdated')} ${this.dateService.formatDate(vehicle.temperatureProbes[index].latestRecord?.Timestamp)}`,
            outOfDate: this.dateService.date1IsAfterDate2(cutOff, vehicle.temperatureProbes[index].latestRecord?.Timestamp)
        };

        return this.vTableService.createTableCell(data, { sortData: latestTemp, exportData: data.temperature, component: StackedProbeReadingComponent, componentInputs: { data: data } })

    }

    private createTabs(): void {
        this.filterTabs = [];

        this.createTabDefinition("Common.All", (r) => true);
        this.createTabDefinition("TempCompliance.OutOfBounds", (r) => r.outOfBounds);
        this.createTabDefinition("ReeferList.Inactive", (r) => r.outOfDate);
        this.createTabDefinition("TempCompliance.InJourney", (r) => r.inJourney);
        this.createTabDefinition("TempCompliance.NoAlarm", (r) => !r.hasProfile);
    }

    private createTabDefinition(translationString: string, callback: (vehicle: IVehicleWithTemperatureData) => boolean) {

        this.filterTabs.push({ title: this.translationService.t(translationString), predicate: callback });
    }

    private probeIsOutOfBounds(probe: ITemperatureProbeWithTemperatureData): boolean {
        const histories = probe.outOfBoundsHistories;
        const isInProfileBounds = this.temperatureProbeService.temperatureIsInBoundsAccordingToProfile(probe);

        if (histories.length && histories.find(history => !history.EndUtc) && !isInProfileBounds) {
            return true;
        }

        return false;
    }
}
